/**
 * Centralized section parsing utilities for consistent heading formats across all components
 * 
 * STANDARD FORMAT:
 * - H1 (#) = Document title (not a section)
 * - H2 (##) = Main sections (used for regeneration, editing, preview)
 * - H3+ (###, ####, etc.) = Subsections within main sections
 */

export interface ParsedSection {
  id: string;
  title: string;
  content: string;
  level: number;
  order: number;
}

export interface TemplateSection {
  id: string;
  title: string;
  content: string;
  edited: boolean;
}

/**
 * Parse markdown content into sections using consistent H2 (##) standard
 * This is the SINGLE SOURCE OF TRUTH for section parsing
 */
export const parseMarkdownSections = (content: string): ParsedSection[] => {
  if (!content) return [];

  const lines = content.split("\n");
  const sections: ParsedSection[] = [];
  let currentSection: ParsedSection | null = null;
  let sectionOrder = 0;
  let contentLines: string[] = [];

  console.log('🔧 Parsing sections with H2 standard from content:', content.substring(0, 100));
  console.log('🔧 Total content length:', content.length);
  console.log('🔧 Total lines:', lines.length);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check for H2 headers (##) - our standard for main sections
    const h2Match = line.match(/^##\s+(.+)$/);
    if (h2Match) {
      // Save previous section if exists
      if (currentSection) {
        const sectionContent = contentLines.join('\n').trim();
        currentSection.content = sectionContent;
        sections.push(currentSection);
        console.log(`💾 Saved section "${currentSection.title}" with ${sectionContent.length} chars`);
      }

      // Start new section
      const title = h2Match[1].trim();
      currentSection = {
        id: `section-${sectionOrder + 1}`,
        title,
        content: '',
        level: 2,
        order: sectionOrder++
      };
      contentLines = [];

      console.log(`📋 Found H2 section: "${title}" (order: ${sectionOrder - 1})`);
    } else if (currentSection) {
      // Add content to current section (including H3+ subsections)
      contentLines.push(line);
    } else {
      // Content before first H2 section - log for debugging
      if (line.trim()) {
        console.log(`⚠️ Content before first H2 section: "${line.substring(0, 50)}..."`);
      }
    }
    // Skip H1 titles and content before first H2 section
  }

  // Add final section
  if (currentSection) {
    const finalSectionContent = contentLines.join('\n').trim();
    currentSection.content = finalSectionContent;
    sections.push(currentSection);
    console.log(`💾 Saved final section "${currentSection.title}" with ${finalSectionContent.length} chars`);
  }

  console.log(`✅ Parsed ${sections.length} sections using H2 standard:`, sections.map(s => s.title));

  // 🚨 DETAILED DEBUG: Show all parsed sections with content lengths
  console.log("🔍 PARSEMARKDOWNSECTIONS DETAILED DEBUG:");
  sections.forEach((section, index) => {
    console.log(`📋 Section ${index + 1}: "${section.title}"`);
    console.log(`   - ID: ${section.id}`);
    console.log(`   - Content length: ${section.content?.length || 0}`);
    console.log(`   - Content preview: ${section.content?.substring(0, 100) || 'NO CONTENT'}...`);
    console.log(`   - Order: ${section.order}`);
  });

  return sections;
};

/**
 * Parse markdown for template sections (YooptaTemplateEditor format)
 */
export const parseMarkdownForTemplateEditor = (content: string): TemplateSection[] => {
  const parsedSections = parseMarkdownSections(content);
  
  return parsedSections.map(section => ({
    id: section.id,
    title: section.title,
    content: section.content,
    edited: false
  }));
};

/**
 * Extract section titles only (for template creation)
 */
export const extractSectionTitles = (content: string): string[] => {
  const sections = parseMarkdownSections(content);
  const titles = sections.map(s => s.title);
  
  console.log("📋 Extracted section titles:", titles);
  return titles.length > 0 ? titles : ["Template Content"];
};

/**
 * Parse sections for regeneration (matches server-side logic)
 */
export const parseMarkdownForRegeneration = (content: string) => {
  const lines = content.split("\n");
  const sections = [];
  let documentTitle = '';

  // First, extract document title (H1) but don't treat it as a section
  const h1Match = lines.find(line => line.match(/^#\s+(.+)$/));
  if (h1Match) {
    documentTitle = h1Match.replace(/^#\s+/, '').trim();
    console.log(`📄 Document title: "${documentTitle}"`);
  }

  // Extract H2 headers as sections (consistent with our standard)
  let currentSection = null;
  let sectionContent = "";

  for (const line of lines) {
    // Skip H1 (document title)
    if (line.match(/^#\s+(.+)$/)) {
      continue;
    }

    // Check for H2 headers as sections (our standard)
    const headerMatch = line.match(/^##\s+(.+)$/);
    if (headerMatch) {
      // Save previous section if exists
      if (currentSection) {
        sections.push({
          id: `section-${sections.length + 1}`,
          title: currentSection,
          content: sectionContent.trim(),
        });
      }

      // Start new section
      const title = headerMatch[1].trim();
      currentSection = title;
      sectionContent = "";
    } else if (currentSection) {
      sectionContent += line + "\n";
    }
  }

  // Add final section if exists
  if (currentSection) {
    sections.push({
      id: `section-${sections.length + 1}`,
      title: currentSection,
      content: sectionContent.trim(),
    });
  }

  console.log(`📊 Parsed ${sections.length} sections for regeneration:`, sections.map(s => s.title));
  return sections.length > 0
    ? sections
    : [
        {
          id: "section-1",
          title: "Document Content",
          content: content,
        },
      ];
};

/**
 * Parse sections for document overview (template selection)
 */
export const parseMarkdownForOverview = (content: string) => {
  const sections = parseMarkdownSections(content);
  
  return sections.map((section, index) => ({
    name: section.title,
    color: ["blue", "green", "yellow", "purple", "indigo", "pink"][index % 6],
    description: `Section from template content`,
  }));
};

/**
 * Convert sections back to markdown with consistent H2 format
 */
export const sectionsToMarkdown = (sections: TemplateSection[]): string => {
  return sections
    .map(section => {
      const content = section.content.trim();
      return `## ${section.title}\n\n${content}`;
    })
    .join('\n\n');
};

/**
 * Validate if content follows our H2 standard
 */
export const validateSectionFormat = (content: string): { isValid: boolean; issues: string[] } => {
  const issues: string[] = [];
  const lines = content.split('\n');
  
  let hasH2Sections = false;
  let hasInconsistentHeaders = false;
  
  for (const line of lines) {
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      
      if (level === 2) {
        hasH2Sections = true;
      } else if (level === 1) {
        // H1 is OK for document title
      } else if (level > 2) {
        // H3+ is OK for subsections
      } else {
        hasInconsistentHeaders = true;
      }
    }
  }
  
  if (!hasH2Sections) {
    issues.push("No H2 (##) sections found. Use ## for main sections.");
  }
  
  if (hasInconsistentHeaders) {
    issues.push("Inconsistent header levels found. Use ## for main sections.");
  }
  
  return {
    isValid: issues.length === 0,
    issues
  };
};

/**
 * Convert content to our standard H2 format
 */
export const normalizeToH2Standard = (content: string): string => {
  const lines = content.split('\n');
  const normalizedLines: string[] = [];
  
  for (const line of lines) {
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const title = headerMatch[2].trim();
      
      if (level === 1) {
        // Keep H1 as document title
        normalizedLines.push(line);
      } else if (level <= 3) {
        // Convert H2 and H3 to H2 (main sections)
        normalizedLines.push(`## ${title}`);
      } else {
        // Convert H4+ to H3 (subsections)
        normalizedLines.push(`### ${title}`);
      }
    } else {
      normalizedLines.push(line);
    }
  }
  
  return normalizedLines.join('\n');
};

/**
 * PolicySection-based utilities for the Create Policy flow (PolicyEditPage)
 * These use the PolicySection type from policyGeneration.ts
 */
import type { PolicySection } from '../types/policyGeneration';

/**
 * Split markdown content into PolicySection[] by H2 headings
 * Used by PolicyEditPage for section-based editing
 */
export const splitMarkdownIntoSections = (markdown: string): PolicySection[] => {
  if (!markdown.trim()) return [];

  const lines = markdown.split('\n');
  const sections: PolicySection[] = [];
  let currentTitle = '';
  let currentContent: string[] = [];
  const preH2Content: string[] = [];
  let sectionIndex = 0;
  let hasH2 = false;
  let inPreH2 = true;

  for (const line of lines) {
    const h2Match = line.match(/^## (.+)/);
    if (h2Match) {
      hasH2 = true;

      if (inPreH2) {
        // Save pre-H2 content as "Introduction" only if there's meaningful body text
        // Skip H1 headers (#) - only include actual body content
        const bodyLines = preH2Content.filter(l => {
          const trimmed = l.trim();
          return trimmed && !trimmed.match(/^#\s+/);
        });
        if (bodyLines.length > 0) {
          sections.push({
            index: sectionIndex,
            title: 'Introduction',
            content: preH2Content.filter(l => !l.trim().match(/^#\s+/)).join('\n').trim(),
            isEdited: false,
          });
          sectionIndex++;
        }
        inPreH2 = false;
      } else if (currentTitle) {
        // Save previous H2 section
        sections.push({
          index: sectionIndex,
          title: currentTitle,
          content: currentContent.join('\n').trim(),
          isEdited: false,
        });
        sectionIndex++;
      }

      currentTitle = h2Match[1].trim();
      currentContent = [];
    } else if (inPreH2) {
      preH2Content.push(line);
    } else {
      currentContent.push(line);
    }
  }

  // Add last section
  if (currentTitle) {
    sections.push({
      index: sectionIndex,
      title: currentTitle,
      content: currentContent.join('\n').trim(),
      isEdited: false,
    });
  }

  // If no H2 headings found, treat entire content as one section
  if (!hasH2 && markdown.trim()) {
    return [{
      index: 0,
      title: 'Document',
      content: markdown.trim(),
      isEdited: false,
    }];
  }

  return sections;
};

/**
 * Merge PolicySection[] back into full markdown
 */
export const mergeSectionsToMarkdown = (sections: PolicySection[]): string => {
  return sections
    .map((section) => {
      const heading = `## ${section.title}`;
      return section.content ? `${heading}\n\n${section.content}` : heading;
    })
    .join('\n\n');
};

/**
 * Add a new section at a specific index
 */
export const addSectionAtIndex = (
  sections: PolicySection[],
  index: number,
  title: string = 'New Section',
  content: string = ''
): PolicySection[] => {
  const newSection: PolicySection = {
    index: 0,
    title,
    content,
    isEdited: true,
  };
  const result = [...sections];
  result.splice(index, 0, newSection);
  // Re-index all sections
  return result.map((s, i) => ({ ...s, index: i }));
};

/**
 * Delete a section by index
 */
export const deleteSectionByIndex = (
  sections: PolicySection[],
  index: number
): PolicySection[] => {
  const result = sections.filter((_, i) => i !== index);
  // Re-index all sections
  return result.map((s, i) => ({ ...s, index: i }));
};

/**
 * Reorder sections by moving a section from one index to another
 */
export const reorderSections = (
  sections: PolicySection[],
  fromIndex: number,
  toIndex: number
): PolicySection[] => {
  if (fromIndex === toIndex) return sections;
  if (fromIndex < 0 || fromIndex >= sections.length) return sections;
  if (toIndex < 0 || toIndex >= sections.length) return sections;

  const result = [...sections];
  const [moved] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, moved);
  // Re-index all sections
  return result.map((s, i) => ({ ...s, index: i }));
};

export default {
  parseMarkdownSections,
  parseMarkdownForTemplateEditor,
  extractSectionTitles,
  parseMarkdownForRegeneration,
  parseMarkdownForOverview,
  sectionsToMarkdown,
  validateSectionFormat,
  normalizeToH2Standard,
  splitMarkdownIntoSections,
  mergeSectionsToMarkdown,
  addSectionAtIndex,
  deleteSectionByIndex,
  reorderSections,
};
